import React, { FC, memo } from 'react';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';

import Menu from 'vibo-ui/Menu';
import Dropdown from 'vibo-ui/Dropdown';
import FormInput from 'components/form/inputs/FormInput';
import PageContentHeader from 'components/common/PageContentHeader';
import Button, { ButtonDecor } from 'vibo-ui/Button';

import { useModal } from 'vibo-ui/Modal';
import { onError } from 'graphql/helpers';
import { toastNotify } from 'graphql/hooks/common';
import { MAX_TITLE_LENGTH } from 'services/constants';
import { useParentPage } from 'graphql/hooks/navigation';
import { updatePlaylistPublic } from 'graphql/cache/songIdeas';
import { copyToClipboard } from 'services/common/stringHelpers';
import { getRedirectPlaylistLink } from 'services/songIdeas/helpers';

import { GET_SONG_IDEAS_DEEP_LINK } from 'graphql/queries/songIdeas';
import { CLONE_SONG_IDEAS, TOGGLE_PUBLIC_SONG_IDEAS } from 'graphql/mutations/songIdeas';

import { IconmoonFont } from 'vibo-ui/Icon';
import { ButtonIconType, Modals } from 'types/enums';
import { SongIdeaPageHeaderProps } from './interfaces';
import { ShareModalProps } from 'components/modals/ShareModal';
import { CloneModalProps } from 'components/modals/CloneModal';
import { DeleteSongIdeasProps } from 'components/modals/DeleteSongIdeasModal';
import { AssigneType, AssignPlaylistModalProps } from 'components/modals/AssignPlaylistModal';

const SongIdeaPageHeader: FC<SongIdeaPageHeaderProps> = ({ songIdeas, currentTab }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { getGoBackParams } = useParentPage();
  const { openModal } = useModal();

  const [cloneSongIdeas] = useMutation(CLONE_SONG_IDEAS, {
    variables: {
      songIdeasId: songIdeas._id,
      payload: {
        title: songIdeas.title,
      },
    },
    onCompleted: ({ cloneSongIdeas }) => {
      navigate(getRedirectPlaylistLink(cloneSongIdeas._id));
      toastNotify({
        text: t('playlistCopiedToMyIdeasSuccessfully'),
        iconType: ButtonIconType.clone,
      });
    },
    onError,
  });

  const playlistDeepLinkData = useQuery(GET_SONG_IDEAS_DEEP_LINK, {
    variables: {
      songIdeasId: songIdeas._id,
    },
    onError,
  });

  const playlistDeepLink = get(playlistDeepLinkData.data, 'getSongIdeasDeepLink');

  const openAssignPlaylistModal = () =>
    openModal<AssignPlaylistModalProps>({
      key: Modals.assignPlaylist,
      props: {
        assigneType: AssigneType.event,
      },
    });
  const handleGetPlaylistDeepLink = () => {
    copyToClipboard(playlistDeepLink, t('copyPlaylistDeeplinkSuccess'));
  };
  const openShareModal = () =>
    openModal<ShareModalProps>({
      key: Modals.share,
      props: {
        mutation: TOGGLE_PUBLIC_SONG_IDEAS,
        variables: {
          songIdeasId: songIdeas._id,
        },
        onCompleted: () => !!songIdeas._id && updatePlaylistPublic(songIdeas),
        option: songIdeas,
        title: t(songIdeas.isPublic ? 'makePlaylistPrivate' : 'makePlaylistPublic'),
        children: t(
          songIdeas.isPublic ? 'makeSongIdeasPrivateModalText' : 'makeSongIdeasPublicModalText'
        ),
      },
    });
  const openClonePlaylistModal = () =>
    openModal<CloneModalProps>({
      key: Modals.clone,
      props: {
        values: {
          title: songIdeas.title,
        },
        onSubmit: payload => {
          cloneSongIdeas?.({
            variables: {
              songIdeasId: songIdeas._id,
              payload,
            },
          });
        },
        title: t(songIdeas.isOwner ? 'clonePlaylist' : 'addToMyPlaylists'),
        label: t('playlistName'),
        closeOnSubmit: true,
      },
    });
  const openDeletePlaylistModal = () =>
    openModal<DeleteSongIdeasProps>({
      key: Modals.deletePlaylist,
      props: {
        playlistId: songIdeas._id,
        playlistTitle: songIdeas.title,
        onSubmit: () => navigate(getGoBackParams(`/songIdeas/${currentTab}`)),
      },
    });

  const menuItems = [
    <Menu.Item
      onClick={openAssignPlaylistModal}
      icon={IconmoonFont['plus-16']}
      key="assignPlaylistTo"
    >
      {t('assignPlaylistTo')}
    </Menu.Item>,
    songIdeas.isOwner && !!songIdeas?.deepLink ? (
      <Menu.Item onClick={handleGetPlaylistDeepLink} icon={IconmoonFont['link-16']} key="copyLink">
        {t('copyLink')}
      </Menu.Item>
    ) : null,
    songIdeas.isOwner ? (
      <Menu.Item onClick={openShareModal} icon={IconmoonFont['publicOutline-16']} key="makePublic">
        {t(songIdeas.isPublic ? 'makePrivate' : 'makePublic')}
      </Menu.Item>
    ) : null,
    <Menu.Item
      onClick={openClonePlaylistModal}
      icon={songIdeas.isOwner ? IconmoonFont['clone-16'] : IconmoonFont['plus-16']}
      key="clonePlaylist"
    >
      {t(songIdeas.isOwner ? 'clonePlaylist' : 'addToMyPlaylists')}
    </Menu.Item>,
    songIdeas.isOwner ? (
      <Menu.Item
        onClick={openDeletePlaylistModal}
        className={ButtonDecor.danger}
        icon={IconmoonFont['delete-16']}
        key="delete"
      >
        {t('deletePlaylist')}
      </Menu.Item>
    ) : null,
  ].filter(Boolean);

  return (
    <PageContentHeader
      extra={
        <Dropdown overlay={<Menu selectable={false}>{menuItems}</Menu>}>
          <Button displayType="bordered" shape="round" size="lg">
            Playlist options
          </Button>
        </Dropdown>
      }
    >
      <FormInput
        disabled={!songIdeas.isOwner}
        maxLength={MAX_TITLE_LENGTH}
        name="title"
        className="bold"
        size="xlg"
        withUnderline
        showMaxLength
      />
    </PageContentHeader>
  );
};

export default memo(SongIdeaPageHeader);
